/**
 * API client for communicating with Cloudflare Worker backend
 */

import type {
  VisitorDataResponse,
  TimePeriod,
  VisitorMetadataSafe,
  SpecimenResult,
  ContributionSummary,
  Achievement,
  AchievementProgress,
  SpecimenWithStatus,
  BotSpecimenWithStatus,
  CollectionStats,
} from "@ephemera/shared";

const API_BASE_URL = import.meta.env.VITE_API_URL || "/api";

export interface VisitResponse {
  sessionId: string;
  isNewSession: boolean;
  timestamp: string;
  isBot: boolean;
  botName: string | null;
  unlockedAchievements: Achievement[];
  visitorMetadata: VisitorMetadataSafe;
  specimenResults: SpecimenResult[];
  contributionSummary: ContributionSummary;
}

export interface CollectionResponseWithProgress {
  period: TimePeriod;
  dateRange: { start: string; end: string };
  specimens: {
    geography: SpecimenWithStatus[];
    technology: SpecimenWithStatus[];
    temporal: SpecimenWithStatus[];
    infrastructure: SpecimenWithStatus[];
    digitalVisitors: BotSpecimenWithStatus[];
  };
  achievements: Achievement[];
  achievementProgress: AchievementProgress[];
  stats: CollectionStats;
}

/**
 * Configuration for retry logic
 */
interface RetryConfig {
  maxRetries: number;
  retryDelay: number;
  retryableStatuses: number[];
}

const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  retryDelay: 1000, // 1 second
  retryableStatuses: [408, 429, 500, 502, 503, 504],
};

class ApiClient {
  private baseUrl: string;
  private retryConfig: RetryConfig;

  constructor(baseUrl: string, retryConfig: Partial<RetryConfig> = {}) {
    this.baseUrl = baseUrl;
    this.retryConfig = { ...DEFAULT_RETRY_CONFIG, ...retryConfig };
  }

  /**
   * Sleep for a specified duration
   */
  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Generic fetch wrapper with error handling and retry logic
   */
  private async fetch<T>(
    endpoint: string,
    options: RequestInit = {},
    retryCount = 0
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const defaultOptions: RequestInit = {
      credentials: "include", // Include cookies
      headers: {
        "Content-Type": "application/json",
      },
    };

    try {
      const response = await fetch(url, { ...defaultOptions, ...options });

      if (!response.ok) {
        // Check if status is retryable
        const shouldRetry =
          retryCount < this.retryConfig.maxRetries &&
          this.retryConfig.retryableStatuses.includes(response.status);

        if (shouldRetry) {
          console.warn(
            `Request failed with status ${response.status}, retrying (${
              retryCount + 1
            }/${this.retryConfig.maxRetries})...`
          );
          await this.sleep(this.retryConfig.retryDelay * (retryCount + 1)); // Exponential backoff
          return this.fetch<T>(endpoint, options, retryCount + 1);
        }

        // Parse error response
        const errorData = await response.json().catch(() => ({
          error: "Unknown error",
          message: response.statusText,
        }));

        // Use museum-themed error messages from server
        const errorMessage =
          errorData.error ||
          errorData.message ||
          `HTTP ${response.status}: ${response.statusText}`;

        throw new Error(errorMessage);
      }

      return response.json();
    } catch (error) {
      // Network error or fetch failed
      if (
        error instanceof TypeError &&
        retryCount < this.retryConfig.maxRetries
      ) {
        console.warn(
          `Network error, retrying (${retryCount + 1}/${
            this.retryConfig.maxRetries
          })...`
        );
        await this.sleep(this.retryConfig.retryDelay * (retryCount + 1));
        return this.fetch<T>(endpoint, options, retryCount + 1);
      }

      // Re-throw if not retryable or max retries exceeded
      throw error;
    }
  }

  /**
   * POST /api/visit - Record a visit
   */
  async recordVisit(): Promise<VisitResponse> {
    return this.fetch<VisitResponse>("/visit", {
      method: "POST",
    });
  }

  /**
   * GET /api/collection?period={period} - Get collection data
   */
  async getCollection(period: TimePeriod = "all"): Promise<CollectionResponseWithProgress> {
    return this.fetch<CollectionResponseWithProgress>(`/collection?period=${period}`);
  }

  /**
   * GET /api/visitor-data - Get visitor's own data
   */
  async getVisitorData(): Promise<VisitorDataResponse> {
    return this.fetch<VisitorDataResponse>("/visitor-data");
  }

  /**
   * Connect to SSE endpoint for real-time updates
   * Returns EventSource instance
   */
  connectSSE(): EventSource {
    const url = `${this.baseUrl}/events`;
    return new EventSource(url, { withCredentials: true });
  }
}

export const apiClient = new ApiClient(API_BASE_URL);
